const formatNumeric = (num: number): string => {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(1) + 'M'
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(1) + 'K'
  }
  return num.toString()
}

const convertSecondsToTime = (seconds: number): string => {
  if (seconds < 60) {
    return `${Math.round(seconds)}s`
  }
  if (seconds < 3600) {
    const minutes = Math.floor(seconds / 60)
    const remainingSeconds = Math.round(seconds % 60)
    return `${minutes}m ${remainingSeconds}s`
  }
  const hours = Math.floor(seconds / 3600)
  const minutes = Math.floor((seconds % 3600) / 60)
  return `${hours}h ${minutes}m`
}

export const formatISODate = (isoString: string): string => {
  const date = new Date(isoString)
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

const utcStringToDDMMYYYY = (utcString: string): string => {
  const date = new Date(utcString)
  const day = date.getDate().toString().padStart(2, '0')
  const month = (date.getMonth() + 1).toString().padStart(2, '0')
  const year = date.getFullYear()
  return `${day}/${month}/${year}`
}

export const getElapsedTime = (startDate: string): string => {
  const start = new Date(startDate)
  const now = new Date()
  const elapsed = now.getTime() - start.getTime()
  const days = Math.floor(elapsed / (1000 * 60 * 60 * 24))
  
  if (days === 0) {
    const hours = Math.floor(elapsed / (1000 * 60 * 60))
    if (hours === 0) {
      const minutes = Math.floor(elapsed / (1000 * 60))
      return `${minutes}m ago`
    }
    return `${hours}h ago`
  }
  
  return `${days}d ago`
}

export const convertUTCToLocalTime = (utcString: string): string => {
  return new Date(utcString).toLocaleTimeString()
}

export const convertUTCtoFormattedDate = (utcString: string): string => {
  return new Date(utcString).toLocaleDateString()
}


export const formatDate = formatISODate // Alias for compatibility

/* -------------------------------------------------------------------------- */
/* Trend-axis bucket labels                                                    */
/* -------------------------------------------------------------------------- */

/** Label shape for one point on a trend x-axis. */
export type TrendBucketFormat = 'hour' | 'hourWithDay' | 'day' | 'month'

/** `9am`, `12pm`, `3pm` — local time, no padding, lowercase meridiem. */
export const formatHourOfDay = (value: string | number | Date): string => {
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)
  const hours = date.getHours()
  const hour12 = hours % 12 === 0 ? 12 : hours % 12
  return `${hour12}${hours < 12 ? 'am' : 'pm'}`
}

const localDayKey = (date: Date): string =>
  `${date.getFullYear()}-${date.getMonth()}-${date.getDate()}`

/**
 * Decide how a trend axis should label its buckets.
 *
 * A single-day custom range (Aug 29 – Aug 29) comes back with **hourly** buckets,
 * so date-only labels render "Aug 29" once per bucket — ten identical ticks.
 * Whenever the buckets are hourly, or they all land on the same calendar day,
 * the axis must carry the time instead.
 *
 * @param values      Bucket timestamps (ISO strings / epoch ms / Dates).
 * @param granularity Server bucket size, when known (`hour` | `day` | `week` | `month`).
 */
export const resolveTrendBucketFormat = (
  values: Array<string | number | Date | null | undefined>,
  granularity?: string | null
): TrendBucketFormat => {
  const dates = values
    .filter((v): v is string | number | Date => v != null)
    .map((v) => (v instanceof Date ? v : new Date(v)))
    .filter((d) => !Number.isNaN(d.getTime()))

  const dayKeys = new Set(dates.map(localDayKey))
  const singleDay = dates.length > 0 && dayKeys.size === 1

  const g = (granularity || '').toLowerCase()

  if (g === 'month') return 'month'
  if (g === 'hour') return singleDay ? 'hour' : 'hourWithDay'
  if (g === 'day' || g === 'week') return 'day'

  // Granularity unknown — infer from the buckets. All on one calendar day means
  // the series can only be intra-day, so label it with hours.
  if (singleDay) return 'hour'
  return 'day'
}

/** Render one trend bucket timestamp with the format chosen above. */
export const formatTrendBucket = (
  value: string | number | Date | null | undefined,
  format: TrendBucketFormat
): string => {
  if (value == null) return ''
  const date = value instanceof Date ? value : new Date(value)
  if (Number.isNaN(date.getTime())) return String(value)

  switch (format) {
    case 'hour':
      return formatHourOfDay(date)
    case 'hourWithDay':
      return `${date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })}, ${formatHourOfDay(date)}`
    case 'month':
      return date.toLocaleDateString(undefined, {
        month: 'short',
        year: 'numeric',
      })
    case 'day':
    default:
      return date.toLocaleDateString(undefined, {
        month: 'short',
        day: 'numeric',
      })
  }
}
